import type { SkinAssetMetadata } from './asset';

/**
 * Where a skin's art goes, as arithmetic (ADR 0041).
 *
 * ## Pure, and platform-neutral
 *
 * No Phaser, no canvas, no DOM. Every function here takes numbers and returns
 * numbers, which is what lets the hunt and the duel share one answer instead of
 * two that drift - and what lets the awkward cases be tested without a browser.
 *
 * ## The problem this solves
 *
 * The ordinary hulls are framed identically: the ship is centred in a square
 * canvas and fills it. A skin is somebody's art, and it is not obliged to be
 * framed that way. Two things follow, and both are handled here rather than in
 * a scene:
 *
 * 1. **The pivot is not always the image centre.** A sprite rotates about its
 *    origin, so art with a long exhaust needs its origin moved - and moving the
 *    origin also moves where the sprite is DRAWN, which has to be compensated
 *    or the hull drifts off the position the simulation owns.
 * 2. **The hull centre is not always the pivot.** The team mark, the health bar
 *    and the magic circle hang off where the SHIP is, which may not be where
 *    the image turns.
 *
 * ## What it never does
 *
 * It never moves the ship. Every function takes the authoritative position as
 * an input and returns where to draw; none of them returns a new position for
 * anything the simulation owns. A skin is presentation.
 */

/** Where and how large to draw one skin's hull sprite. */
export interface SkinSpritePlacement {
  /** Normalised origin to hand the sprite. `pivot`, straight through. */
  readonly originX: number;
  readonly originY: number;
  /**
   * World position for the SPRITE, which is not always the ship's position.
   *
   * When the pivot and the hull centre differ, drawing at the ship's position
   * would put the hull centre somewhere else - so the sprite is offset by the
   * gap between them, rotated into world space. When they are equal, which is
   * the ordinary case, this is exactly the ship's position.
   */
  readonly x: number;
  readonly y: number;
  /** Uniform scale factor for the sprite. */
  readonly scale: number;
  /** The rotation to hand the sprite, in radians. */
  readonly rotation: number;
}

/**
 * How many world units one hull diameter is, for a skin at a given scale.
 *
 * `baseScale` is the author's correction for art framed with margin: a ship
 * that fills 70% of its canvas would otherwise be drawn 30% small, because both
 * scenes size art by the texture's own pixels.
 */
export function skinHullDiameter(
  asset: SkinAssetMetadata,
  hullWorldUnits: number,
  visualScale: number,
): number {
  return hullWorldUnits * visualScale * asset.baseScale;
}

/**
 * The uniform scale to hand a sprite so the hull covers `hullWorldUnits`.
 *
 * Divides by the texture's own pixel height, exactly as `HuntScene` and
 * `pvpSpriteScale` already do, so art delivered at any resolution lands at the
 * same world size. The three official masters are square, but nothing here
 * assumes that: the height is what both scenes measure.
 */
export function skinSpriteScale(
  asset: SkinAssetMetadata,
  textureHeight: number,
  hullWorldUnits: number,
  visualScale: number,
): number {
  if (!Number.isFinite(textureHeight) || textureHeight <= 0) {
    // A texture with no height cannot be measured. Falling back to the visual
    // scale keeps the ship visible at roughly the right size rather than
    // collapsing it to nothing, which is the failure a player would report.
    return visualScale * asset.baseScale;
  }
  return (hullWorldUnits * visualScale * asset.baseScale) / textureHeight;
}

/**
 * Rotates a hull-local offset into world space.
 *
 * `forward` is along the nose and `lateral` to the ship's right, both in hull
 * DIAMETERS - the same reading `pvpCannonMounts` uses. `rotation` is the
 * sprite's world rotation, which for nose-up art is `heading + PI/2`.
 */
export function skinHullOffsetToWorld(
  offset: { readonly forward: number; readonly lateral: number },
  hullDiameter: number,
  rotation: number,
): { x: number; y: number } {
  /*
   * The art points UP, so its local "forward" is -Y before rotation. Writing it
   * out rather than reusing `slotToWorld` because that helper is expressed in
   * the simulation's convention, where forward is +X, and quietly mixing the
   * two is exactly how a battery ends up at right angles to its ship.
   */
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const localX = offset.lateral * hullDiameter;
  const localY = -offset.forward * hullDiameter;
  return { x: localX * cos - localY * sin, y: localX * sin + localY * cos };
}

/**
 * Everything needed to draw one skin's hull sprite this frame.
 *
 * `x`/`y` is the AUTHORITATIVE position of the ship, and it comes out
 * unchanged unless the art's pivot and hull centre disagree - in which case the
 * sprite is nudged so the hull centre lands on the authoritative point.
 */
export function skinSpritePlacement(input: {
  readonly asset: SkinAssetMetadata;
  readonly textureWidth: number;
  readonly textureHeight: number;
  /** Where the SHIP is, from the simulation or the room. Never changed. */
  readonly x: number;
  readonly y: number;
  readonly rotation: number;
  readonly hullWorldUnits: number;
  readonly visualScale: number;
}): SkinSpritePlacement {
  const { asset } = input;
  const scale = skinSpriteScale(
    asset,
    input.textureHeight,
    input.hullWorldUnits,
    input.visualScale,
  );

  /*
   * The gap between where the image turns and where the ship IS, in pixels of
   * the source image, scaled into world units and rotated with the sprite.
   *
   * Zero for every skin whose pivot and hull centre agree, which is all three
   * of the official masters today - so this arithmetic costs nothing in the
   * ordinary case and is the reason the awkward case is expressible at all.
   */
  const dxPixels = (asset.pivot.x - asset.hullCenter.x) * input.textureWidth;
  const dyPixels = (asset.pivot.y - asset.hullCenter.y) * input.textureHeight;
  const dx = dxPixels * scale;
  const dy = dyPixels * scale;
  const cos = Math.cos(input.rotation);
  const sin = Math.sin(input.rotation);

  return {
    originX: asset.pivot.x,
    originY: asset.pivot.y,
    x: input.x + (dx * cos - dy * sin),
    y: input.y + (dx * sin + dy * cos),
    scale,
    rotation: input.rotation,
  };
}

/*
 * There is deliberately no `skinCannonAnchor` here (gate V23.1C).
 *
 * It used to compute where a skin says a cannon assembly is bolted, and its own
 * documentation described the purpose plainly: a skin "may move the ORIGIN that
 * formation is measured from". That is exactly what the product has since
 * decided a skin must never do - cannons, hardpoints, muzzles and projectiles
 * stay where the ship puts them, whatever art is worn.
 *
 * It had no caller outside its own tests. Left in place it was a loaded gun:
 * an exported helper, already written, that does the forbidden thing correctly.
 */

/**
 * The visible extent of the art, in world units, from its declared bounds.
 *
 * Used to decide whether an overlay's spread is still ON the ship rather than
 * beside it, and to report in a capture what the art actually occupies. It is
 * NOT a hitbox and nothing that reads it may treat it as one - the collision
 * radius is the server's and this cannot move it.
 */
export function skinVisualExtent(
  asset: SkinAssetMetadata,
  textureWidth: number,
  textureHeight: number,
  scale: number,
): { readonly width: number; readonly height: number } {
  return {
    width: (asset.visualBounds.right - asset.visualBounds.left) * textureWidth * scale,
    height: (asset.visualBounds.bottom - asset.visualBounds.top) * textureHeight * scale,
  };
}

/**
 * A point on the texture, placed in the world by the SAME transform the sprite
 * uses (gate V27A).
 *
 * ## Why this has to exist rather than be re-derived
 *
 * The body sparkle is born at points the pipeline read out of the master's
 * alpha, as fractions of that texture. For a particle to land on paint, the
 * renderer has to apply exactly the placement the hull sprite got - the same
 * pivot, the same world-units-per-pixel, the same rotation, the same offset
 * between the pivot and the hull centre. A second copy of that arithmetic
 * would be a second opinion, and the first frame the two disagreed the sparkle
 * would drift off the ship.
 *
 * So the placement is an INPUT here. Whatever the scene handed the sprite is
 * what this consumes; there is no path by which a particle can be positioned
 * against a transform the hull is not drawn with.
 *
 * ## The arithmetic
 *
 * `(u, v)` are fractions of the texture. Their offset from the sprite's origin,
 * in source pixels, is `((u - pivot.x) * width, (v - pivot.y) * height)`;
 * `placement.scale` turns pixels into world units, and the rotation is the
 * sprite's own. `placement.x/y` already carries the pivot-to-hull-centre
 * correction, which is why a point AT `hullCenter` comes back exactly at the
 * ship's authoritative position.
 *
 * Presentation only: this decides where a light is drawn and is read by nothing
 * that resolves a hit.
 */
export function skinTexturePointToWorld(input: {
  readonly point: { readonly x: number; readonly y: number };
  readonly placement: SkinSpritePlacement;
  readonly textureWidth: number;
  readonly textureHeight: number;
}): { readonly x: number; readonly y: number } {
  const { placement } = input;
  const px = (input.point.x - placement.originX) * input.textureWidth * placement.scale;
  const py = (input.point.y - placement.originY) * input.textureHeight * placement.scale;
  const cos = Math.cos(placement.rotation);
  const sin = Math.sin(placement.rotation);
  return {
    x: placement.x + (px * cos - py * sin),
    y: placement.y + (px * sin + py * cos),
  };
}

/**
 * The inverse, so a test can ask "which pixel is this light standing on".
 *
 * Exact: a rotation and a uniform scale are both invertible, so the round trip
 * is the identity to floating point. That is what makes "the mask and the
 * sprite agree" a thing a machine can check rather than a paragraph.
 */
export function skinWorldPointToTexture(input: {
  readonly point: { readonly x: number; readonly y: number };
  readonly placement: SkinSpritePlacement;
  readonly textureWidth: number;
  readonly textureHeight: number;
}): { readonly x: number; readonly y: number } {
  const { placement } = input;
  const dx = input.point.x - placement.x;
  const dy = input.point.y - placement.y;
  const cos = Math.cos(-placement.rotation);
  const sin = Math.sin(-placement.rotation);
  const px = dx * cos - dy * sin;
  const py = dx * sin + dy * cos;
  return {
    x: px / placement.scale / input.textureWidth + placement.originX,
    y: py / placement.scale / input.textureHeight + placement.originY,
  };
}
